import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  Typography,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { onlyofficeService } from "../services/api";

const loadScript = (src) => {
  return new Promise((resolve, reject) => {
    // Check if script already loaded
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error("Falha ao carregar o editor OnlyOffice"));
    document.head.appendChild(script);
  });
};

const OnlyOfficeEditor = () => {
  const { id: fileId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const editorRef = useRef(null);

  const handleBack = useCallback(() => {
    navigate(`/files/${fileId}`);
  }, [navigate, fileId]);

  useEffect(() => {
    let destroyed = false;

    const initEditor = async () => {
      try {
        // 1. Fetch config from backend
        const response = await onlyofficeService.getConfig(fileId);
        const { config, onlyofficeUrl } = response.data;

        if (destroyed) return;

        // 2. Load OnlyOffice API script
        await loadScript(
          `${onlyofficeUrl}/web-apps/apps/api/documents/api.js`
        );

        if (destroyed) return;

        // 3. Initialize editor
        editorRef.current = new window.DocsAPI.DocEditor(
          "onlyoffice-editor",
          {
            ...config,
            height: "100%",
            width: "100%",
          }
        );

        setLoading(false);
      } catch (err) {
        if (destroyed) return;
        const message =
          err.response?.data?.error ||
          err.message ||
          "Erro ao carregar o editor";
        setError(message);
        setLoading(false);
      }
    };

    initEditor();

    return () => {
      destroyed = true;
      if (editorRef.current) {
        try {
          editorRef.current.destroyEditor();
        } catch (e) {
          // Editor may already be destroyed
        }
        editorRef.current = null;
      }
    };
  }, [fileId]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100vw",
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 1300,
        bgcolor: "background.default",
      }}
    >
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar variant="dense">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Voltar
          </Button>
          <Typography variant="subtitle1" color="text.secondary">
            Editor de Documentos
          </Typography>
        </Toolbar>
      </AppBar>

      {loading && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography color="text.secondary">
            Carregando editor...
          </Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ p: 3 }}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={handleBack}>
                Voltar
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      )}

      <Box
        id="onlyoffice-editor"
        sx={{
          flex: 1,
          display: loading || error ? "none" : "block",
        }}
      />
    </Box>
  );
};

export default OnlyOfficeEditor;
